/**
 * Viewport Observer Hook - Optimizes performance by pausing animations when not visible
 * Uses IntersectionObserver to detect when components enter/leave viewport
 */

import { useEffect, useRef, useState, useCallback } from 'react';

interface ViewportObserverOptions {
  threshold?: number;
  rootMargin?: string;
  enabled?: boolean;
}

interface ViewportObserverReturn {
  ref: React.RefObject<HTMLElement>;
  isVisible: boolean;
  isNearViewport: boolean;
  visibilityRatio: number;
  pauseAnimations: boolean;
}

export function useViewportObserver({
  threshold = 0.1,
  rootMargin = '100px',
  enabled = true,
}: ViewportObserverOptions = {}): ViewportObserverReturn {
  const ref = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isNearViewport, setIsNearViewport] = useState(false);
  const [visibilityRatio, setVisibilityRatio] = useState(0);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const entry = entries[0];
    if (!entry) return;

    const ratio = entry.intersectionRatio;
    const isIntersecting = entry.isIntersecting;
    
    setIsVisible(isIntersecting && ratio >= threshold);
    setIsNearViewport(isIntersecting || ratio > 0);
    setVisibilityRatio(ratio);
  }, [threshold]);

  useEffect(() => {
    if (!enabled || !ref.current) return;

    // Create intersection observer with multiple thresholds for smooth transitions
    const thresholds = Array.from({ length: 21 }, (_, i) => i * 0.05); // 0, 0.05, 0.1, ..., 1.0
    
    observerRef.current = new IntersectionObserver(handleIntersection, {
      threshold: thresholds,
      rootMargin,
    });

    observerRef.current.observe(ref.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [enabled, handleIntersection, rootMargin]);

  // Determine if animations should be paused based on visibility
  const pauseAnimations = !isVisible && !isNearViewport;

  return {
    ref,
    isVisible,
    isNearViewport,
    visibilityRatio,
    pauseAnimations,
  };
}

/**
 * Performance-aware viewport observer with frame rate throttling
 */
interface PerformanceViewportOptions extends ViewportObserverOptions {
  highPerformanceMode?: boolean;
  frameRateThreshold?: number;
}

export function usePerformanceViewport({
  highPerformanceMode = false,
  frameRateThreshold = 30,
  ...options
}: PerformanceViewportOptions = {}): ViewportObserverReturn & {
  shouldUseHighQuality: boolean;
  recommendedFrameRate: number;
} {
  const viewport = useViewportObserver(options);
  const [currentFPS, setCurrentFPS] = useState(60);
  const frameCountRef = useRef(0);
  const lastTimeRef = useRef(performance.now());

  // Monitor frame rate
  useEffect(() => {
    let animationId: number;

    const measureFPS = () => {
      const now = performance.now();
      frameCountRef.current++;

      if (now - lastTimeRef.current >= 1000) {
        const fps = Math.round((frameCountRef.current * 1000) / (now - lastTimeRef.current));
        setCurrentFPS(fps);
        frameCountRef.current = 0;
        lastTimeRef.current = now;
      }

      animationId = requestAnimationFrame(measureFPS);
    };

    if (viewport.isVisible) {
      measureFPS();
    }

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [viewport.isVisible]);

  // Determine quality and frame rate based on performance
  const shouldUseHighQuality = highPerformanceMode && currentFPS > frameRateThreshold && viewport.isVisible;
  const recommendedFrameRate = viewport.isVisible 
    ? currentFPS > 45 ? 60 : 30
    : viewport.isNearViewport ? 15 : 0;

  return {
    ...viewport,
    shouldUseHighQuality,
    recommendedFrameRate,
  };
}

/**
 * Batch viewport observer for multiple elements
 */
export function useBatchViewportObserver<T extends string>(
  elementIds: T[],
  options: ViewportObserverOptions = {}
): Record<T, ViewportObserverReturn> {
  const refs = useRef<Record<string, React.RefObject<HTMLElement>>>(
    Object.fromEntries(elementIds.map(id => [id, { current: null }]))
  );
  
  const [visibilityStates, setVisibilityStates] = useState<Record<T, {
    isVisible: boolean;
    isNearViewport: boolean;
    visibilityRatio: number;
  }>>(
    Object.fromEntries(elementIds.map(id => [id, {
      isVisible: false,
      isNearViewport: false,
      visibilityRatio: 0,
    }])) as Record<T, any>
  );

  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const updates: Partial<typeof visibilityStates> = {};

    entries.forEach(entry => {
      const elementId = entry.target.getAttribute('data-viewport-id') as T;
      if (!elementId) return;

      const ratio = entry.intersectionRatio;
      const isIntersecting = entry.isIntersecting;
      const threshold = options.threshold || 0.1;

      updates[elementId] = {
        isVisible: isIntersecting && ratio >= threshold,
        isNearViewport: isIntersecting || ratio > 0,
        visibilityRatio: ratio,
      };
    });

    if (Object.keys(updates).length > 0) {
      setVisibilityStates(prev => ({ ...prev, ...updates }));
    }
  }, [options.threshold]);

  useEffect(() => {
    if (!options.enabled !== false) {
      const thresholds = Array.from({ length: 21 }, (_, i) => i * 0.05);
      
      observerRef.current = new IntersectionObserver(handleIntersection, {
        threshold: thresholds,
        rootMargin: options.rootMargin || '100px',
      });

      // Observe all elements
      elementIds.forEach(id => {
        const element = refs.current[id]?.current;
        if (element) {
          element.setAttribute('data-viewport-id', id);
          observerRef.current!.observe(element);
        }
      });
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [elementIds, handleIntersection, options.enabled, options.rootMargin]);

  // Create return object with refs and computed pause states
  const result = {} as Record<T, ViewportObserverReturn>;
  
  elementIds.forEach(id => {
    const state = visibilityStates[id];
    result[id] = {
      ref: refs.current[id],
      isVisible: state.isVisible,
      isNearViewport: state.isNearViewport,
      visibilityRatio: state.visibilityRatio,
      pauseAnimations: !state.isVisible && !state.isNearViewport,
    };
  });

  return result;
}